import { useEffect, useState, useCallback } from "react";
import {
  Book,
  DocumentText1,
  Message,
  MessageQuestion,
  Profile2User,
  Teacher,
  UserSearch,
} from "iconsax-reactjs";
import { DashboardCard } from "../../components/partials/dashboard/DashboardCard";
import { DashboardCardSkeleton } from "../../components/partials/dashboard/DashboardCardSkeleton";
import type { GetDashboardDataResponse } from "../../services/webServices/dahsboard/types";
import { getDashboardData } from "../../services/webServices/dahsboard/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const cardsConfig = [
  {
    key: "users",
    title: "کل کاربران",
    dataKey: "users_count" as const,
    icon: <UserSearch size="22" color="currentColor" variant="Bold" />,
    accentClass: "hover:border-primaryVar-200",
    iconBgClass: "bg-primaryVar-50 text-primaryVar-600",
  },
  {
    key: "teachers",
    title: "معلمان",
    dataKey: "teachers_count" as const,
    icon: <Teacher size="22" color="currentColor" variant="Bold" />,
    accentClass: "hover:border-blueVar-200",
    iconBgClass: "bg-blueVar-50 text-blueVar-600",
  },
  {
    key: "students",
    title: "دانش‌آموزان",
    dataKey: "students_count" as const,
    icon: <Profile2User size="22" color="currentColor" variant="Bold" />,
    accentClass: "hover:border-successVar-light",
    iconBgClass: "bg-successVar-light text-successVar-dark",
  },
  {
    key: "articles",
    title: "مقالات",
    dataKey: "articles_count" as const,
    icon: <Book size="22" color="currentColor" variant="Bold" />,
    accentClass: "hover:border-warningVar-light",
    iconBgClass: "bg-warningVar-light text-warningVar-dark",
  },
  {
    key: "posts",
    title: "پست‌ها",
    dataKey: "posts_count" as const,
    icon: <DocumentText1 size="22" color="currentColor" variant="Bold" />,
    accentClass: "hover:border-infoVar-light",
    iconBgClass: "bg-infoVar-light text-infoVar-dark",
  },
  {
    key: "questions",
    title: "سوالات",
    dataKey: "questions_count" as const,
    icon: <MessageQuestion size="22" color="currentColor" variant="Bold" />,
    accentClass: "hover:border-secondaryVar-200",
    iconBgClass: "bg-secondaryVar-50 text-secondaryVar-600",
  },
  {
    key: "answers",
    title: "پاسخ‌ها",
    dataKey: "answers_count" as const,
    icon: <Message size="22" color="currentColor" variant="Bold" />,
    accentClass: "hover:border-errorVar-light",
    iconBgClass: "bg-errorVar-light text-errorVar-dark",
  },
];

export default function Dashboard() {
  const [dashboardData, setDashboardData] =
    useState<GetDashboardDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchDashboardData = useCallback(async () => {
    if (isFirstLoad) showLoading();
    setIsLoading(true);

    try {
      const data = await getDashboardData();
      setDashboardData(data);
    } finally {
      setIsLoading(false);
      if (isFirstLoad) hideLoading();
    }
  }, [hideLoading, isFirstLoad, showLoading]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="flex w-full flex-col gap-6">
      <DashboardHeader />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? Array.from({ length: cardsConfig.length }).map((_, index) => (
              <DashboardCardSkeleton key={index} />
            ))
          : cardsConfig.map((card) => (
              <DashboardCard
                key={card.key}
                title={card.title}
                value={dashboardData?.[card.dataKey] ?? 0}
                icon={card.icon}
                accentClass={card.accentClass}
                iconBgClass={card.iconBgClass}
              />
            ))}
      </div>
    </div>
  );
}

function DashboardHeader() {
  return (
    <div className="rounded-2xl border border-gray-100 bg-gradient-to-l from-primaryVar-50 via-white to-white p-6 shadow-sm">
      <h1 className="text-xl font-bold text-gray-900">داشبورد</h1>
      <p className="mt-1 text-sm text-gray-500">
        نمای کلی از وضعیت کاربران و محتوای پلتفرم
      </p>
    </div>
  );
}
